import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type EmailTemplateRow,
  useMissedCount,
  useSetTemplateActive,
} from "@/hooks/use-email-templates";

interface ReactivateDialogProps {
  template: EmailTemplateRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Switching a review email back on. Anyone who became due it while it was off
 * is still eligible, so the admin chooses: send it to them now (the default —
 * the next sender pass picks them up), or skip them for good.
 */
export function ReactivateDialog({ template, open, onOpenChange }: ReactivateDialogProps) {
  const missed = useMissedCount(template.key, open);
  const setActive = useSetTemplateActive(template.key);
  const [choice, setChoice] = useState<"send" | "skip">("send");
  const count = missed.data;
  const busy = setActive.isPending;

  async function confirm() {
    // With nobody waiting there is nothing to choose; send the plain flip.
    const args = count === 0 ? { active: true } : { active: true, missed: choice };
    // The hook toasts the error itself; this only closes the dialog on success.
    await setActive
      .mutateAsync(args)
      .then(() => onOpenChange(false))
      .catch(() => undefined);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Switch on “{template.label}”</DialogTitle>
          <DialogDescription>
            Choose what happens for people who were due this email while it was off.
          </DialogDescription>
        </DialogHeader>

        {missed.isFetching ? (
          <LoadingRows />
        ) : missed.isError ? (
          <ErrorState
            message={missed.error?.message ?? "Could not count who is waiting"}
            onRetry={() => missed.refetch()}
          />
        ) : count === 0 ? (
          <p className="text-sm">
            Nobody is waiting for this email, so switching it on changes nothing today.
          </p>
        ) : count !== undefined ? (
          <div className="space-y-3 text-sm">
            <p className="font-medium">
              {count} {count === 1 ? "person" : "people"} would receive this email
            </p>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="missed"
                  checked={choice === "send"}
                  disabled={busy}
                  onChange={() => setChoice("send")}
                />
                Send it to them
              </label>
              <p className="pl-6 text-muted-foreground text-xs">
                They'll get it on the next scheduled send, within six hours.
              </p>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="missed"
                  checked={choice === "skip"}
                  disabled={busy}
                  onChange={() => setChoice("skip")}
                />
                Skip them — they won't get it
              </label>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || missed.isFetching || count === undefined} onClick={confirm}>
            {busy ? "Switching on…" : "Switch on"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
